import React, { useEffect, useState, useRef, lazy } from 'react';
import { AlertCircle, CheckCircle2, ShieldCheck, TrendingUp, FileText, Users, Globe, Award, ArrowRight, Briefcase, ChevronRight, Factory, HardHat, Stethoscope, Truck, Building2, Landmark, Shield, MessageCircle, Phone, Mail, MapPin, Clock } from 'lucide-react';
export default function LandingPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const problemsRef = useRef<HTMLElement>(null);
  const solutionsRef = useRef<HTMLElement>(null);
  const [problemsVisible, setProblemsVisible] = useState(false);
  const [solutionsVisible, setSolutionsVisible] = useState(false);
  const videoUrl = 'https://cdn.magicpatterns.com/uploads/sKAXjqFp1wbCizSjgrrWTu/VIDEO-2025-02-07-07-12-27.mp4#t=0.1';
  const logoUrl = 'https://cdn.magicpatterns.com/uploads/i7oVmgzV6mzJdDFXdarJqH/image.png';
  useEffect(() => {
    const timer = setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(error => {
          console.log('El navegador bloqueó el autoplay:', error);
        });
      }
    }, 4000);
    // Observer for Problems section
    const problemsObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setProblemsVisible(true);
        problemsObserver.disconnect(); // Only animate once
      }
    }, {
      threshold: 0.2
    });
    if (problemsRef.current) {
      problemsObserver.observe(problemsRef.current);
    }
    // Observer for Solutions section
    const solutionsObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setSolutionsVisible(true);
        solutionsObserver.disconnect(); // Only animate once
      }
    }, {
      threshold: 0.3
    });
    if (solutionsRef.current) {
      solutionsObserver.observe(solutionsRef.current);
    }
    return () => {
      clearTimeout(timer);
      problemsObserver.disconnect();
      solutionsObserver.disconnect();
    };
  }, []);
  return <div className="min-h-screen bg-white text-slate-800 font-sans selection:bg-red-100 selection:text-red-900" data-id="element-1" data-mp="3ni0t4vxj5kyr">
      {/* NAVBAR PREMIUM */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-brand-dark/95 backdrop-blur-md border-b border-white/10 transition-all duration-300" data-id="element-2" data-mp="3ipznhsuvashp">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3" data-mp="3enw7cqx96zq3">
          <div className="flex justify-between items-center h-24" data-id="element-4" data-mp="39s770ldetesq">
            <div className="flex-shrink-0 flex items-center" data-id="element-5" data-mp="34r9p89wbamrr">
              <img src={logoUrl} alt="Standard Consulting Logo" className="h-12 w-auto mix-blend-screen" data-id="element-6" data-mp="34tvw07bdoy8x" />
            </div>
            <div className="hidden md:flex items-center space-x-8" data-id="element-7" data-mp="2vsxrlnx12opt">
              <a href="#servicios" className="text-sm text-slate-300 hover:text-white font-medium tracking-wide transition-colors" data-id="element-8" data-mp="xzs5dz933s7l">
                SERVICIOS
              </a>
              <a href="#nosotros" className="text-sm text-slate-300 hover:text-white font-medium tracking-wide transition-colors" data-id="element-9" data-mp="2bwutwfcpgaaa">
                NOSOTROS
              </a>
              <a href="#contacto" className="text-sm text-slate-300 hover:text-white font-medium tracking-wide transition-colors" data-id="element-10" data-mp="2lpacnevtt2w8">
                CONTACTO
              </a>
              <a href="#contacto" className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold py-3 px-8 rounded-full text-sm tracking-wide transition-all shadow-[0_0_15px_rgba(239,68,68,0.5)] hover:shadow-[0_0_25px_rgba(239,68,68,0.8)] transform hover:-translate-y-0.5" data-id="element-11" data-mp="1xwtr2ucvqss6">
                DIAGNÓSTICO GRATUITO
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* 1. HERO SECTION PREMIUM */}
      <section className="relative pt-40 pb-24 px-4 sm:px-6 lg:px-8 text-center overflow-hidden bg-[#0B0F19]" data-id="element-12" data-mp="3gv8908877k8r">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-[#111827] to-[#0B0F19] z-0" data-id="element-13" data-mp="3bz16kpf0gqvk"></div>

        {/* Subtle Finance Grid Texture (Increased opacity for more glow) */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff15_1px,transparent_1px),linear-gradient(to_bottom,#ffffff15_1px,transparent_1px)] bg-[size:40px_40px] z-0" data-id="element-14" data-mp="3ltt9m8rd45xq"></div>

        {/* Red Spotlight Behind Title */}
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-600/20 via-red-900/5 to-transparent blur-[80px] pointer-events-none z-0" data-id="element-15" data-mp="df1cqms44pa0"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent z-0" data-id="element-16" data-mp="hh64ffz03bu0"></div>

        <div className="relative z-10 max-w-5xl mx-auto" data-id="element-17" data-mp="112es7ovbjcvx">
          <div className="inline-flex items-center space-x-2 mb-8 border border-white/10 bg-white/5 px-4 py-1.5 rounded-full backdrop-blur-sm shadow-lg" data-id="element-18" data-mp="110vb5w1u8xt3">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.8)]" data-id="element-19" data-mp="s186n1dtbbvt"></span>
            <span className="text-xs font-medium tracking-widest uppercase text-slate-300" data-id="element-20" data-mp="w1s3e3uh4dhf">
              Firma de Auditoría y Consultoría
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-tight mb-8 text-white drop-shadow-lg" data-id="element-21" data-mp="n3yirq62n8a3">
            Transformamos la información financiera en{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-red-600 italic drop-shadow-none" data-id="element-22" data-mp="dcc3niqm4p22">
              confianza, transparencia y decisiones estratégicas.
            </span>
          </h1>

          <div className="relative w-full max-w-5xl mx-auto mb-12 rounded-lg overflow-hidden shadow-[0_0_40px_rgba(220,38,38,0.15)] border border-white/20 bg-slate-900 aspect-video group ring-1 ring-white/10 p-1 bg-gradient-to-b from-slate-800 to-slate-900" data-id="element-23" data-mp="4bw0kads7jxq">
            <div className="absolute inset-0 border border-white/5 z-20 pointer-events-none rounded-lg" data-id="element-24" data-mp="1wba003cdhmrx"></div>
            <video ref={videoRef} src={videoUrl} controls playsInline preload="metadata" className="w-full h-full object-cover relative z-10 rounded-md shadow-inner" data-id="element-25" data-mp="1sazibtf7x7x8">
              Tu navegador no soporta el elemento de video.
            </video>
          </div>

          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed drop-shadow-md" data-id="element-26" data-mp="2g1lqdkp82s78">
            Acompañamos a su empresa con revisoría fiscal, auditoría y
            consultoría bajo estándares internacionales (NIIF y NIAS),
            fortaleciendo su gobierno corporativo y generando valor sostenible.
          </p>

          <a href="#contacto" className="inline-flex bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold py-4 px-10 rounded-full text-lg transition-all duration-300 shadow-[0_0_25px_rgba(239,68,68,0.4)] hover:shadow-[0_0_40px_rgba(239,68,68,0.7)] items-center justify-center mx-auto group tracking-wide transform hover:-translate-y-1" data-id="element-27" data-mp="3bafjhz1phs6m">
            SOLICITAR DIAGNÓSTICO GRATUITO
            <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-28" data-mp="2sje53hyktmf" />
          </a>
        </div>
      </section>

      {/* 2. SECCIÓN PROBLEMAS (DOLOR) */}
      <section ref={problemsRef} className="py-24 bg-slate-50 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-29" data-mp="btqy0gjq9rwz">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000006_1px,transparent_1px),linear-gradient(to_bottom,#00000006_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-30" data-mp="29llkcghck0td"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-0" data-id="element-31" data-mp="1popjsi16rh0z"></div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-32" data-mp="23gnqynbc72ui">
          <div className={`text-center mb-16 transition-all duration-1000 transform ${problemsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-33" data-mp="1zf272i0dxoat">
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-6" data-id="element-34" data-mp="16r6areeq9yek">
              ¿Te identificas con alguna de estas situaciones?
            </h2>
            <p className="text-slate-500 text-lg font-light tracking-wide" data-id="element-35" data-mp="1kobcx3bbmd4j">
              LOS DESCUIDOS FINANCIEROS HOY, SON LAS CRISIS DE MAÑANA.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8" data-id="element-36" data-mp="2w4009vshle5v">
            {['¿Cuenta con información financiera confiable y oportuna para la toma de decisiones?', '¿Su organización refleja altos estándares de transparencia y gobierno corporativo?', '¿Está aprovechando la auditoría como una herramienta estratégica y no solo de cumplimiento?', '¿Sus procesos financieros están alineados con estándares internacionales?', '¿Tiene claridad sobre los riesgos y oportunidades de su negocio?'].map((problem, idx, arr) => <div key={idx} className={`bg-white p-8 rounded-sm shadow-sm border border-slate-100 border-t-4 border-t-red-600 flex items-start space-x-5 hover:shadow-md transition-all duration-700 transform ${idx === arr.length - 1 && arr.length % 2 !== 0 ? 'sm:col-span-2 sm:max-w-2xl sm:mx-auto w-full' : ''} ${problemsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
            transitionDelay: `${idx * 150}ms`
          }} data-id="element-37" data-mp="3if1ps38m2uxp" data-mpi={idx}>
                <div className="mt-1" data-id="element-38" data-mp="2d3i6jzz8eqxx">
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" data-id="element-39" data-mp="2d4tco6g5gas7" />
                </div>
                <p className="text-slate-700 font-medium text-lg leading-relaxed" data-id="element-40" data-mp="2i1beoyk3hv0u">
                  {problem}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. SECCIÓN SOLUCIÓN */}
      <section ref={solutionsRef} className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden" data-id="element-41" data-mp="293rd1oiblwpp">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000004_1px,transparent_1px),linear-gradient(to_bottom,#00000004_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-42" data-mp="1z51r9knazs46"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0B0F19] to-transparent z-0" data-id="element-43" data-mp="1bdwcz9x0h7vz"></div>

        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-16 relative z-10" data-id="element-44" data-mp="1gbxxlza35f9k">
          <div className={`lg:w-1/2 transition-all duration-1000 transform ${solutionsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} data-id="element-45" data-mp="wrz9ul46kwgx">
            <div className="w-12 h-1 bg-red-600 mb-8" data-id="element-46" data-mp="16qog1gmpsshc"></div>
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-8 leading-tight" data-id="element-47" data-mp="11qk3g1oxghwe">
              Te ayudamos a tomar el control total de tu empresa
            </h2>
            <p className="text-lg text-slate-600 mb-10 font-light leading-relaxed" data-id="element-48" data-mp="1fnyx5im7rbu6">
              Con información clara, cumplimiento legal estricto y estrategias
              financieras inteligentes, transformamos la incertidumbre en
              seguridad, rentabilidad y crecimiento sostenido.
            </p>
            <ul className="space-y-6" data-id="element-49" data-mp="1ygjj0g3hwgoh">
              {['Revisoría fiscal independiente que fortalece la transparencia, el gobierno corporativo y la confianza de inversionistas y stakeholders', 'Cumplimiento normativo integral y prevención de riesgos', 'Optimización tributaria dentro del marco legal', 'Protección patrimonial con visión de largo plazo', 'Información financiera confiable para la toma de decisiones estratégicas'].map((solution, idx) => <li key={idx} className={`flex items-start space-x-4 text-slate-800 font-medium transition-all duration-700 transform ${solutionsVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`} style={{
              transitionDelay: `${400 + idx * 150}ms`
            }} data-id="element-50" data-mp="2g8mwugt33e5l" data-mpi={idx}>
                  <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" data-id="element-51" data-mp="2z4v5lulb275n" />
                  <span className="text-lg leading-relaxed" data-id="element-52" data-mp="zu3smrxigyms">{solution}</span>
                </li>)}
            </ul>
          </div>
          <div className={`lg:w-1/2 w-full relative transition-all duration-1000 delay-300 transform ${solutionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-53" data-mp="14u9dnp99zmvw">
            {/* Premium image treatment with offset border */}
            <div className="absolute inset-0 border-2 border-red-600 translate-x-4 translate-y-4 rounded-sm hidden md:block" data-id="element-54" data-mp="39awfmch6t56s"></div>
            <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Equipo de consultoría trabajando" loading="lazy" className="relative z-10 rounded-sm shadow-2xl w-full object-cover aspect-[4/3] grayscale-[20%]" data-id="element-55" data-mp="348xd14t9ucaw" />
          </div>
        </div>
      </section>

      {/* 4. SERVICIOS */}
      <section id="servicios" className="py-24 bg-brand-dark text-white px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-56" data-mp="2gl5zjxvzvt81">
        {/* Subtle Dark Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-57" data-mp="2bop7islocupo"></div>

        {/* Top fade from previous section */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0B0F19] to-transparent z-0" data-id="element-58" data-mp="3qjnntntcrfgd"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent z-0" data-id="element-59" data-mp="n1dsyty1tj1a"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-60" data-mp="r1xqhymjaony">
          <div className="text-center mb-20" data-id="element-61" data-mp="d3px20opi5hj">
            <h2 className="text-3xl md:text-5xl font-serif font-medium mb-6" data-id="element-62" data-mp="d2y12or765qq">
              Nuestros Servicios Especializados
            </h2>
            <p className="text-red-400 text-sm md:text-base font-semibold tracking-widest uppercase" data-id="element-63" data-mp="2xtos59knkk2q">
              Soluciones diseñadas para proteger, optimizar y hacer crecer tu
              empresa
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-20" data-id="element-64" data-mp="2v8h8ul7zpkyn">
            {/* Servicio 1 */}
            <div className="bg-slate-900/50 border border-white/10 p-10 rounded-sm hover:border-red-500/50 transition-colors group" data-id="element-65" data-mp="2qaz0ox1sr9rp">
              <ShieldCheck className="w-10 h-10 text-red-500 mb-8 group-hover:scale-110 transition-transform" data-id="element-66" data-mp="349f7enznt09u" />
              <h3 className="text-2xl font-serif font-medium mb-4" data-id="element-67" data-mp="2z8rge2bfkjfm">
                Auditoría y Revisoría Fiscal
              </h3>
              <p className="text-slate-400 mb-8 font-light leading-relaxed" data-id="element-68" data-mp="27jy24a4l9g8o">
                Garantizamos la transparencia y cumplimiento legal de tu
                empresa, detectando riesgos antes de que se conviertan en
                sanciones.
              </p>
              <ul className="text-sm text-slate-300 space-y-4" data-id="element-69" data-mp="2gi9q04e2lzxf">
                <li className="flex items-start" data-id="element-70" data-mp="1stqktxqbi39k">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-71" data-mp="1nvp078d8tvvz" />
                  <span data-id="element-72" data-mp="1ntunuig1fc3i">Detección temprana de riesgos financieros</span>
                </li>
                <li className="flex items-start" data-id="element-73" data-mp="3nvu5wcydaxe4">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-74" data-mp="3svzsvftozvpv" />
                  <span data-id="element-75" data-mp="1i3ur7957pl1">Cumplimiento con normativas DIAN</span>
                </li>
                <li className="flex items-start" data-id="element-76" data-mp="6h6woitttfos">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-77" data-mp="6gngp7nql1de" />
                  <span data-id="element-78" data-mp="350ndnj5xlj36">Informes claros para toma de decisiones</span>
                </li>
                <li className="flex items-start" data-id="element-79" data-mp="39yynvv051vlx">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-80" data-mp="3ex9wy3im0233" />
                  <span data-id="element-81" data-mp="3iyvg26lqlclq">Prevención de sanciones y multas</span>
                </li>
              </ul>
            </div>

            {/* Servicio 2 */}
            <div className="bg-slate-900/50 border border-white/10 p-10 rounded-sm hover:border-red-500/50 transition-colors group" data-id="element-82" data-mp="u69dxpkjt6ir">
              <TrendingUp className="w-10 h-10 text-red-500 mb-8 group-hover:scale-110 transition-transform" data-id="element-83" data-mp="yapinnife7we" />
              <h3 className="text-2xl font-serif font-medium mb-4" data-id="element-84" data-mp="2isi0i52eitjb">
                Asesoría Tributaria y Financiera
              </h3>
              <p className="text-slate-400 mb-8 font-light leading-relaxed" data-id="element-85" data-mp="29xk06cwxcehy">
                Diseñamos estrategias legales para optimizar tu carga tributaria
                y mejorar la rentabilidad de tu negocio.
              </p>
              <ul className="text-sm text-slate-300 space-y-4" data-id="element-86" data-mp="201h0ddpjory6">
                <li className="flex items-start" data-id="element-87" data-mp="1v0t8yqxem93f">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-88" data-mp="1v371as0p977a" />
                  <span data-id="element-89" data-mp="1r1lkivl1o8rt">Planeación tributaria inteligente</span>
                </li>
                <li className="flex items-start" data-id="element-90" data-mp="1m1z7on94oujj">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-91" data-mp="3ld8rthl4i1p3" />
                  <span data-id="element-92" data-mp="3gep173vl41qe">Reducción legal de impuestos</span>
                </li>
                <li className="flex items-start" data-id="element-93" data-mp="3gckzmsl5reta">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-94" data-mp="2pa4l1udd02bp" />
                  <span data-id="element-95" data-mp="2u8fwg9jaykw1">Análisis financiero estratégico</span>
                </li>
                <li className="flex items-start" data-id="element-96" data-mp="2fjs0i25jo95k">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-97" data-mp="2fj062unoohgv" />
                  <span data-id="element-98" data-mp="2kgs4m7iu31u1">Acompañamiento en decisiones clave</span>
                </li>
              </ul>
            </div>

            {/* Servicio 3 */}
            <div className="bg-slate-900/50 border border-white/10 p-10 rounded-sm hover:border-red-500/50 transition-colors group" data-id="element-99" data-mp="2akouaedtvww0">
              <FileText className="w-10 h-10 text-red-500 mb-8 group-hover:scale-110 transition-transform" data-id="element-100" data-mp="1wlpg60nxz0e3" />
              <h3 className="text-2xl font-serif font-medium mb-4" data-id="element-101" data-mp="21k0q0be8lawb">
                Outsourcing Contable y Administrativo
              </h3>
              <p className="text-slate-400 mb-8 font-light leading-relaxed" data-id="element-102" data-mp="3vxia1offnpml">
                Nos encargamos de tu contabilidad para que tú te enfoques en
                hacer crecer tu empresa.
              </p>
              <ul className="text-sm text-slate-300 space-y-4" data-id="element-103" data-mp="1fblkx463q444">
                <li className="flex items-start" data-id="element-104" data-mp="1f8z5kicyykbc">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-105" data-mp="1u11a4fpzi1hp" />
                  <span data-id="element-106" data-mp="1p8zo4230kweh">Contabilidad al día y sin errores</span>
                </li>
                <li className="flex items-start" data-id="element-107" data-mp="230eckofoxpsx">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-108" data-mp="22yteiou63oqb" />
                  <span data-id="element-109" data-mp="1y11m7tpm20ll">Reportes financieros claros</span>
                </li>
                <li className="flex items-start" data-id="element-110" data-mp="2cxsr2ii204il">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-111" data-mp="2cwhlcd51smou" />
                  <span data-id="element-112" data-mp="27xwkqclvw0zv">Gestión de nómina y procesos administrativos</span>
                </li>
                <li className="flex items-start" data-id="element-113" data-mp="imx7kl5bz511">
                  <ChevronRight className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-114" data-mp="dmhqpjlubfeb" />
                  <span data-id="element-115" data-mp="26q9uoguev1g2">Optimización operativa</span>
                </li>
              </ul>
            </div>
          </div>

          {/* OTRAS SOLUCIONES ESPECIALIZADAS */}
          <div className="border-t border-white/10 pt-20" data-id="element-116" data-mp="2gguz4kwrn06w">
            <h3 className="text-xl font-serif font-medium text-center mb-12 text-white" data-id="element-117" data-mp="2ggbfmzrgw3qr">
              Otras Soluciones Especializadas
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-118" data-mp="1nrnrmwfc309i">
              {[{
              title: 'Auditoría Financiera',
              desc: 'Evaluamos la salud económica de tu empresa con precisión'
            }, {
              title: 'Auditoría de Gestión',
              desc: 'Medimos la eficiencia y eficacia de tus operaciones'
            }, {
              title: 'Auditoría de Sistemas',
              desc: 'Protegemos tu seguridad de la información'
            }, {
              title: 'Auditoría en Salud',
              desc: 'Cumplimiento normativo para el sector salud'
            }, {
              title: 'Protección Patrimonial',
              desc: 'Estrategias legales para blindar tus activos'
            }, {
              title: 'Avalúo de Activos',
              desc: 'Valoración precisa y certificada de tus bienes'
            }, {
              title: 'Valoración de Empresas',
              desc: 'Determinamos el valor real de tu negocio'
            }, {
              title: 'Asesoría Aduanera',
              desc: 'Optimización en operaciones de comercio exterior'
            }].map((sol, idx) => <div key={idx} className="bg-transparent border border-white/5 p-6 rounded-sm hover:bg-white/5 transition-colors group" data-id="element-119" data-mp="h9o4ela09ezu" data-mpi={idx}>
                  <h4 className="font-bold text-white mb-2 text-sm uppercase tracking-wider group-hover:text-red-400 transition-colors" data-id="element-120" data-mp="vwwipuym20g1">
                    {sol.title}
                  </h4>
                  <p className="text-slate-400 text-sm font-light leading-relaxed" data-id="element-121" data-mp="mvf3sfwxw4sx">
                    {sol.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 5. SOBRE NOSOTROS & EQUIPO */}
      <section id="nosotros" className="py-24 bg-slate-50 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-122" data-mp="87271gq12hxd">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000006_1px,transparent_1px),linear-gradient(to_bottom,#00000006_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-123" data-mp="45oy2xrn1ym8"></div>

        {/* Top fade from previous section */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-slate-50 to-transparent z-0" data-id="element-124" data-mp="3ldaljdbsc134"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-0" data-id="element-125" data-mp="3g8qaumiqohgg"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-126" data-mp="3l7a1h08a2hf5">
          {/* 5.1 Titular y Descripción */}
          <div className="text-center max-w-4xl mx-auto mb-20" data-id="element-127" data-mp="3q7fvgn20sg0e">
            <div className="inline-block border border-red-200 bg-red-50 text-red-600 font-semibold px-4 py-1.5 rounded-full text-xs mb-6 uppercase tracking-widest" data-id="element-128" data-mp="3q4jzh49qg1el">
              Sobre Nosotros
            </div>
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-8 leading-tight" data-id="element-129" data-mp="32hbukng7uud3">
              Más que consultores, somos aliados estratégicos de tu empresa
            </h2>
            <p className="text-lg text-slate-600 font-light leading-relaxed" data-id="element-130" data-mp="29so5shw9dmus">
              En Standard Consulting ayudamos a empresas a crecer con seguridad
              financiera, cumplimiento legal y decisiones estratégicas
              inteligentes. Nuestro compromiso es ofrecer soluciones claras,
              confiables y adaptadas a cada negocio.
            </p>
          </div>

          {/* 5.2 Bloque de Confianza */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24" data-id="element-131" data-mp="u2dq2rq1l7uf">
            {[{
            icon: Globe,
            text: 'Experiencia en múltiples sectores económicos'
          }, {
            icon: Users,
            text: 'Equipo multidisciplinario altamente calificado'
          }, {
            icon: Award,
            text: 'Especialistas en NIIF y normativas internacionales'
          }, {
            icon: Shield,
            text: 'Enfoque en integridad, transparencia y resultados'
          }].map((item, idx) => <div key={idx} className="bg-white p-6 rounded-sm shadow-sm border border-slate-100 flex flex-col items-center text-center hover:shadow-md transition-shadow" data-id="element-132" data-mp="39sq57jerzs34" data-mpi={idx}>
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-4 border border-slate-100" data-id="element-133" data-mp="1u9sbmjbjdn7p">
                  <item.icon className="w-6 h-6 text-red-600" data-id="element-134" data-mp="1fk3323146gxe" />
                </div>
                <p className="text-slate-700 font-medium text-sm leading-relaxed" data-id="element-135" data-mp="1kieegi724zhq">
                  {item.text}
                </p>
              </div>)}
          </div>

          {/* 5.3 A quiénes ayudamos (Industrias) */}
          <div className="bg-brand-dark rounded-sm p-12 md:p-16 mb-24 relative overflow-hidden" data-id="element-136" data-mp="1bgodakndim6r">
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 blur-[80px] rounded-full pointer-events-none" data-id="element-137" data-mp="wo4oyq4vuj9d"></div>
            <div className="relative z-10 flex flex-col lg:flex-row items-center gap-12" data-id="element-138" data-mp="2r01bq5sdr0y3">
              <div className="lg:w-1/3" data-id="element-139" data-mp="2vym9zzo2najw">
                <h3 className="text-2xl md:text-3xl font-serif font-medium text-white mb-4" data-id="element-140" data-mp="2vxkzbwaywq2p">
                  A quiénes ayudamos
                </h3>
                <div className="w-12 h-1 bg-red-600 mb-6" data-id="element-141" data-mp="2pim5izvugr85"></div>
                <p className="text-slate-400 font-light leading-relaxed" data-id="element-142" data-mp="2phb06vmr3bex">
                  Trabajamos con empresas de diferentes sectores económicos,
                  entendiendo las necesidades específicas de cada industria.
                </p>
              </div>
              <div className="lg:w-2/3 grid grid-cols-2 sm:grid-cols-3 gap-6 w-full" data-id="element-143" data-mp="3d8gidhg5yfse">
                {[{
                icon: Factory,
                name: 'Manufactura'
              }, {
                icon: HardHat,
                name: 'Construcción'
              }, {
                icon: Stethoscope,
                name: 'Salud'
              }, {
                icon: Truck,
                name: 'Transporte'
              }, {
                icon: Building2,
                name: 'Servicios'
              }, {
                icon: Landmark,
                name: 'Sector Financiero'
              }].map((industry, idx) => <div key={idx} className="flex items-center space-x-3 bg-white/5 border border-white/10 p-4 rounded-sm hover:bg-white/10 transition-colors" data-id="element-144" data-mp="1msct4ennwrq0" data-mpi={idx}>
                    <industry.icon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-145" data-mp="21n0yztwz5s65" />
                    <span className="text-slate-200 text-sm font-medium" data-id="element-146" data-mp="26dzfathyqq9e">
                      {industry.name}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* 5.4 Equipo */}
          <div className="mb-32" data-id="element-147" data-mp="2fg8o9exl2bko">
            <div className="text-center mb-20" data-id="element-148" data-mp="2fcuomegb0ggx">
              <h3 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-8" data-id="element-149" data-mp="2ketnbb13mp7j">
                Equipo Especializado
              </h3>
              <div className="text-slate-500 font-light text-lg max-w-3xl mx-auto space-y-4 leading-relaxed" data-id="element-150" data-mp="1qbiigwgyoyxw">
                <p data-id="element-151" data-mp="24b1eiqwi0jtu">
                  Nuestro equipo está conformado por profesionales altamente
                  calificados en auditoría, revisoría fiscal y consultoría, con
                  sólida formación técnica y visión estratégica del negocio.
                </p>
                <p data-id="element-152" data-mp="1ci10vh7u5wai">
                  Combinamos experiencia local con estándares internacionales
                  (NIIF y NIAS), aportando análisis rigurosos, independencia
                  profesional y un enfoque orientado a la generación de valor.
                </p>
                <p data-id="element-153" data-mp="1gk5r05z2sase">
                  Acompañamos a nuestros clientes más allá del cumplimiento,
                  brindando claridad, confianza y soporte en la toma de
                  decisiones clave.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-154" data-mp="eq2mm9gl6iur">
              {[{
              name: 'Byron Montoya',
              role: 'Founder & CEO',
              desc: 'Asesora organizaciones en consultoría, auditoría y revisoría fiscal con enfoque estratégico y estándares internacionales (NIIF, NIAS, US GAAP). Aporta una visión integral en control, gobierno corporativo y toma de decisiones, con experiencia en firmas Big Four y diversos sectores economicos.',
              image: 'https://cdn.magicpatterns.com/uploads/4sFQTvyfcaqAYhrZyT8EYQ/image.png',
              alwaysColor: true
            }, {
              name: 'Mauricio Ceballos',
              role: 'Audit & Assurance | CFO',
              desc: 'Integra visión financiera y estratégica para fortalecer la toma de decisiones y la sostenibilidad empresarial. Cuenta con experiencia en auditoría y revisoría fiscal en diversos sectores, con enfoque en estándares internacionales y gestión financiera.',
              image: 'https://cdn.magicpatterns.com/uploads/qWnzBKUjjFG3KXGTg5s79G/image.png',
              alwaysColor: true
            }, {
              name: 'Sabi Briceño',
              role: 'Audit & Assurance - Consulting',
              desc: 'Especialista en auditoría con enfoque en sistemas de información y control interno. Apoya a las organizaciones en el fortalecimiento de sus procesos financieros y en la generación de información confiable.',
              image: 'https://cdn.magicpatterns.com/uploads/1Uej3QCqg8hW4jsTwC6Qgc/sabi_briceno.png',
              alwaysColor: true
            }, {
              name: 'Eliana Robledo',
              role: 'Audit & Assurance - Consulting',
              desc: 'Especialista en auditoría y revisoría fiscal, con experiencia en el sector real y entidades sin ánimo de lucro. Contribuye a la transparencia y cumplimiento mediante un enfoque técnico orientado a la confiabilidad de la información financiera.',
              image: 'https://cdn.magicpatterns.com/uploads/k1jfynKXyWiJBah32QcGi9/Eliana_robledo.png',
              alwaysColor: true
            }, {
              name: 'Daniela Gutiérrez',
              role: 'Audit & Assurance',
              desc: 'Acompaña organizaciones en procesos de auditoría y gobierno corporativo, aportando una visión estructurada y alineada con estándares internacionales. Cuenta con experiencia en firmas Big Four y conocimiento en derecho societario y tributación internacional.',
              image: 'https://cdn.magicpatterns.com/uploads/3SYxM8wzDueVhnxvAWeByU/daniel_gutierrez.png',
              alwaysColor: true
            }, {
              name: 'Daniel Serna',
              role: 'Consulting & Audit Manager',
              desc: 'Lidera procesos de auditoría y consultoría, apoyando a las organizaciones en la implementación de estándares internacionales y en la mejora de sus procesos financieros y de control.',
              image: 'https://cdn.magicpatterns.com/uploads/fxPBC4hedQAsGqGhnjVuWN/daniel_serna.png',
              alwaysColor: true
            }, {
              name: 'Alejandro González',
              role: 'Tax Manager',
              desc: 'Asesora en materia tributaria con un enfoque estratégico y legal, integrando su formación como contador y abogado. Cuenta con experiencia en liderazgo fiscal en el sector salud y en la gestión de impuestos corporativos.',
              image: 'https://cdn.magicpatterns.com/uploads/paWXzqbK15JoDCQX7PyFTC/alejandro.png',
              alwaysColor: true
            }, {
              name: 'Adriana Mesa',
              role: 'Manager T.I.',
              desc: 'Ingeniera de Sistemas con experiencia en procesos de revisoría fiscal en firmas Big Four, empresas del sector real y entidades sin ánimo de lucro. Especialista en la integración de tecnología y auditoría, aporta valor mediante la evaluación de controles de TI, gestión de riesgos tecnológicos y fortalecimiento de entornos digitales seguros.',
              image: 'https://cdn.magicpatterns.com/uploads/6XJXKnx5FNGGw8BHc6o2e3/adriana.png',
              alwaysColor: true
            }].map((member, idx) => <div key={idx} className="bg-white rounded-lg shadow-md shadow-slate-200/50 border border-slate-100 p-8 text-center hover:shadow-2xl hover:shadow-slate-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-155" data-mp="w8uxfxbji2dl" data-mpi={idx}>
                  <div className="w-32 h-32 mx-auto rounded-full bg-slate-200 relative overflow-hidden mb-6 border-4 border-white shadow-lg group-hover:border-red-50 transition-colors" data-id="element-156" data-mp="22tdz9s37tqrd">
                    {member.image ? <img src={member.image} alt={member.name} loading="lazy" className={`w-full h-full object-cover transition-all duration-500 scale-100 group-hover:scale-105 ${member.alwaysColor ? '' : 'grayscale group-hover:grayscale-0'}`} data-id="element-157" data-mp="m8lr0m6b7268" /> : member.bgPos ? <div className={`w-full h-full transition-all duration-500 scale-100 group-hover:scale-105 bg-no-repeat ${member.alwaysColor ? '' : 'grayscale group-hover:grayscale-0'}`} style={{
                  backgroundImage: `url('https://cdn.magicpatterns.com/uploads/eUNAGi7Ar5nEKfaRLVWXMs/image.png')`,
                  backgroundPosition: member.bgPos,
                  backgroundSize: member.bgSize
                }} data-id="element-158" data-mp="14ybmzclt5tzq"></div> : <img src={`https://images.unsplash.com/photo-${1573496359142 + (member.placeholderIdx || 0)}-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80`} alt={member.name} loading="lazy" className="w-full h-full object-cover transition-all duration-500 scale-100 group-hover:scale-105 grayscale group-hover:grayscale-0" data-id="element-159" data-mp="2yjx1usdaxga9" />}
                  </div>
                  <div data-id="element-160" data-mp="38frnzxgqhths">
                    <h4 className="font-serif font-medium text-slate-900 text-xl mb-1 group-hover:text-red-600 transition-colors" data-id="element-161" data-mp="38gjmbg1pu5br">
                      {member.name}
                    </h4>
                    <p className="text-red-600 text-xs font-bold uppercase tracking-wider mb-4" data-id="element-162" data-mp="4yhxbu44adyd">
                      {member.role}
                    </p>
                    <p className="text-slate-600 text-sm font-light leading-relaxed" data-id="element-163" data-mp="nxd0ndca6bwd">
                      {member.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* 5.5 Nuestro Enfoque (Diferencial) */}
          <div className="bg-slate-100 border border-slate-200 p-10 md:p-16 rounded-sm text-center max-w-4xl mx-auto" data-id="element-164" data-mp="j2qo36ugqdap">
            <ShieldCheck className="w-12 h-12 text-red-600 mx-auto mb-6" data-id="element-165" data-mp="575092zytzjv" />
            <h3 className="text-2xl font-serif font-medium text-slate-900 mb-6" data-id="element-166" data-mp="a56k3q57u2we">
              Nuestro enfoque
            </h3>
            <p className="text-lg md:text-xl text-slate-700 font-light leading-relaxed italic" data-id="element-167" data-mp="3sbukj2pbm1vq">
              "No solo auditamos tu empresa. La protegemos, optimizamos y la
              preparamos para crecer de forma sostenible, con estrategias
              financieras inteligentes y cumplimiento total."
            </p>
          </div>
        </div>
      </section>

      {/* 5.5 ALIADOS ESTRATÉGICOS (PRUEBA SOCIAL) */}
      <section className="py-24 bg-white px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-168" data-mp="3b0pdu0mjyh3i">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000004_1px,transparent_1px),linear-gradient(to_bottom,#00000004_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-169" data-mp="3oscc26021zox"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 to-transparent z-0" data-id="element-170" data-mp="3tr6sj1ajvc5v"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10" data-id="element-171" data-mp="bjzjwzqlk35p">
          <div className="mb-16" data-id="element-172" data-mp="6m7hhbsbsyn9">
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-slate-900 mb-6" data-id="element-173" data-mp="6n948dipf9kt">
              Aliados estratégicos que respaldan nuestro trabajo
            </h2>
            <p className="text-lg text-slate-500 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-174" data-mp="28dud5qn5xhq2">
              Hemos trabajado con empresas de diferentes sectores, brindando
              soluciones confiables en auditoría, consultoría y asesoría
              financiera.
            </p>
          </div>

          <div className="mb-12" data-id="element-175" data-mp="1w9olwiwfpqww">
            <img src="https://cdn.magicpatterns.com/uploads/pg5dWpPQKN2HawQvCHsvci/image.png" alt="Logos de empresas aliadas y clientes" loading="lazy" className="w-full max-w-4xl mx-auto rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 object-contain bg-white" data-id="element-176" data-mp="1wahuzqiv8si7" />
          </div>

          <div className="max-w-3xl mx-auto" data-id="element-177" data-mp="1difcsaqkt45u">
            <p className="text-slate-600 font-medium mb-8" data-id="element-178" data-mp="1dljlnlezko2u">
              Trabajamos con empresas de múltiples sectores, generando
              confianza, cumplimiento y crecimiento sostenible para nuestros
              clientes.
            </p>

            <div className="pt-8 border-t border-slate-100" data-id="element-179" data-mp="2twxc2r7dro58">
              <p className="text-sm text-slate-400 uppercase tracking-widest mb-4 font-semibold" data-id="element-180" data-mp="iadf977ow1wq">
                Experiencia en sectores como:
              </p>
              <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-3 text-slate-700 font-medium" data-id="element-181" data-mp="dbscp14yt654">
                <span data-id="element-182" data-mp="8ih2qr4uw98z">Construcción</span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-183" data-mp="3m0hq5syeb04"></span>
                <span data-id="element-184" data-mp="3vp9s8i3xqba5">Salud</span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-185" data-mp="3vpjfhxxli4c5"></span>
                <span data-id="element-186" data-mp="3qqgagdhm7y46">Transporte</span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-187" data-mp="3qpdnuhrbshu2"></span>
                <span data-id="element-188" data-mp="3lqah8svp63jz">Servicios</span>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-189" data-mp="3lrc21p2im4ew"></span>
                <span data-id="element-190" data-mp="3gtakj8kqmd5j">Sector financiero</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. PRUEBA SOCIAL (TESTIMONIOS) */}
      <section className="py-24 bg-slate-50 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-191" data-mp="1f04ax9rpvv44">
        {/* Subtle Light Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000006_1px,transparent_1px),linear-gradient(to_bottom,#00000006_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-192" data-mp="23jkt7jjaz57b"></div>

        {/* Bottom fade out to match next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0B0F19] to-transparent z-0" data-id="element-193" data-mp="1pirtmke59jxc"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-194" data-mp="2n4z2w7le80g1">
          <div className="text-center mb-20" data-id="element-195" data-mp="2i6xklovsk55m">
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-slate-900 mb-6" data-id="element-196" data-mp="2i88k3ci7eqqx">
              Resultados que hablan por nosotros
            </h2>
            <div className="w-12 h-1 bg-red-600 mx-auto" data-id="element-197" data-mp="28hdvi1hhjf2b"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-198" data-mp="28f9txq726s57">
            {[{
            name: 'Ricardo Rodriguez',
            role: 'CEO y Fundador',
            videoUrl: 'https://cdn.magicpatterns.com/uploads/xbpkgzr8uAcXBFgBh9XbpZ/RICARDO%20RODRIGUEZ%20(1).mp4#t=0.001'
          }, {
            name: 'Felipe Ceballos',
            role: 'Empresario y CEO de Grupo Empresarial',
            videoUrl: 'https://cdn.magicpatterns.com/uploads/fWihg2UEm6ZLbPBgaYomv6/FELIPE%20CEBALLOS%20(1).mp4#t=0.001'
          }, {
            name: 'Nickol Verdesoto',
            role: 'Directora Financiera (CFO)',
            videoUrl: 'https://cdn.magicpatterns.com/uploads/4YgWZFBdoUTNVbRQL7duPm/NICKOL%20VERDESOTO%20(1)%20(1).mp4#t=0.001'
          }, {
            name: 'Daniela Sanchez',
            role: 'Socia Inversionista',
            videoUrl: 'https://cdn.magicpatterns.com/uploads/cC6dAgMpGen7V8uquC4naS/DANIELA%20SANCHEZ%20(1).mp4#t=0.001'
          }].map((testimonial, idx) => <div key={idx} className="bg-white rounded-xl shadow-xl shadow-slate-200/40 border border-slate-100 overflow-hidden group" data-id="element-199" data-mp="ixmekm8syio9" data-mpi={idx}>
                <div className="aspect-video bg-slate-900 relative" data-id="element-200" data-mp="2azfdnolnlzme">
                  {testimonial.videoUrl ? <video src={testimonial.videoUrl} controls playsInline preload="metadata" className="w-full h-full object-cover" data-id="element-201" data-mp="2ourg6i61wudv">
                      Tu navegador no soporta el elemento de video.
                    </video> : <div className="w-full h-full flex items-center justify-center bg-slate-800 text-slate-500" data-id="element-202" data-mp="37pombzgkcloe">
                      <span className="font-medium" data-id="element-203" data-mp="1ddrs5yqql1pn">Video próximamente</span>
                    </div>}
                </div>
                <div className="p-6 border-t border-slate-100" data-id="element-204" data-mp="19d7wz0pq4865">
                  <p className="font-bold text-slate-900 tracking-wide text-lg" data-id="element-205" data-mp="z881ixa2gerh">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-red-600 font-semibold uppercase tracking-wider" data-id="element-206" data-mp="138jhhwrhhqwm">
                    {testimonial.role}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. CONTACTO DIRECTO */}
      <section id="contacto" className="py-24 bg-brand-dark px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-207" data-mp="fj7a92fpvswy">
        {/* Subtle Dark Grid Texture */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:40px_40px] z-0 pointer-events-none" data-id="element-208" data-mp="6ju0e8nol2bp"></div>

        {/* Top fade from previous section */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0B0F19] to-transparent z-0" data-id="element-209" data-mp="20yd0k4kr2tqk"></div>

        {/* Decorative background element */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/5 blur-[120px] rounded-full pointer-events-none z-0" data-id="element-210" data-mp="5uop8bxf52ty"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/5 blur-[120px] rounded-full pointer-events-none z-0" data-id="element-211" data-mp="w52i9b03n0j"></div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-212" data-mp="3o0bhbrrw6tf9">
          {/* 7.1 Titular y Subtítulo */}
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-213" data-mp="3j1gri5vic4ln">
            <h2 className="text-4xl md:text-5xl font-serif font-medium text-white mb-6" data-id="element-214" data-mp="3j31n7ytk5tl3">
              Contáctanos y recibe tu diagnóstico financiero
            </h2>
            <p className="text-xl text-slate-400 font-light leading-relaxed" data-id="element-215" data-mp="39bx9f2fmceru">
              Nuestro equipo está listo para ayudarte a proteger y hacer crecer
              tu empresa.
            </p>
          </div>

          {/* 7.2 Botones de Acción Principales */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-20" data-id="element-216" data-mp="2vauk8gtbuocp">
            <a href="https://wa.me/573146152758" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto flex items-center justify-center space-x-3 bg-[#25D366] hover:bg-[#1ebd5b] text-white font-bold py-5 px-8 rounded-full text-lg transition-all duration-300 shadow-[0_0_20px_rgba(37,211,102,0.3)] hover:shadow-[0_0_30px_rgba(37,211,102,0.5)] transform hover:-translate-y-1" data-id="element-217" data-mp="1atllhjaqmx5e">
              <MessageCircle className="w-6 h-6" data-id="element-218" data-mp="s0r7osoupb2r" />
              <span data-id="element-219" data-mp="w3nwj14zi3f5">Agendar por WhatsApp</span>
            </a>
            <a href="tel:+573146152758" className="w-full sm:w-auto flex items-center justify-center space-x-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold py-5 px-8 rounded-full text-lg transition-all duration-300 shadow-[0_0_20px_rgba(239,68,68,0.3)] hover:shadow-[0_0_30px_rgba(239,68,68,0.5)] transform hover:-translate-y-1" data-id="element-220" data-mp="w22y30fju0c0">
              <Phone className="w-6 h-6" data-id="element-221" data-mp="1gobuovdh2t4f" />
              <span data-id="element-222" data-mp="1gnaket4a6anr">Llamar ahora</span>
            </a>
          </div>

          {/* 7.3 Información de Contacto Organizada */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16" data-id="element-223" data-mp="1bnxwe51oaxjl">
            {/* Columna 1: Contacto Principal */}
            <div className="bg-slate-900/80 border border-white/10 rounded-sm p-8 backdrop-blur-sm" data-id="element-224" data-mp="1lfk2xo32g8g0">
              <h3 className="text-xl font-serif font-medium text-white mb-6 flex items-center" data-id="element-225" data-mp="xpzhmyjfwz3k">
                <Phone className="w-5 h-5 text-red-500 mr-3" data-id="element-226" data-mp="sqwh9reenh1x" />
                Líneas de Atención
              </h3>
              <div className="space-y-6" data-id="element-227" data-mp="16r784ufpsvn3">
                <div data-id="element-228" data-mp="16s8rrnasqqgf">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2" data-id="element-229" data-mp="16ob912s4s0x2">
                    Colombia (PBX)
                  </p>
                  <p className="text-lg text-slate-300 font-medium" data-id="element-230" data-mp="2i51gsqc7d0ss">
                    (604) 591 5681
                  </p>
                  <p className="text-lg text-slate-300 font-medium" data-id="element-231" data-mp="2d683oz0tvbt5">
                    (+57) 314 615 2758
                  </p>
                </div>
                <div className="pt-4 border-t border-white/5" data-id="element-232" data-mp="2pp3uldw73cwt">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2" data-id="element-233" data-mp="2pn94ll62gulv">
                    Estados Unidos (Miami)
                  </p>
                  <p className="text-lg text-slate-300 font-medium" data-id="element-234" data-mp="2gnvwavtoic2q">
                    +1 954 795 41 98
                  </p>
                </div>
                <div className="pt-4 border-t border-white/5" data-id="element-235" data-mp="34gcmjtnklad4">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2" data-id="element-236" data-mp="39c1fvf8zxv0z">
                    Correo Electrónico
                  </p>
                  <p className="text-sm text-slate-300 font-medium break-all" data-id="element-237" data-mp="2znjjrbbnwc3o">
                    mauricioceballos@standardconsulting.com.co
                  </p>
                </div>
              </div>
            </div>

            {/* Columna 2: Sedes Colombia */}
            <div className="bg-slate-900/80 border border-white/10 rounded-sm p-8 backdrop-blur-sm lg:col-span-2" data-id="element-238" data-mp="3s55c98dlmlza">
              <h3 className="text-xl font-serif font-medium text-white mb-6 flex items-center" data-id="element-239" data-mp="1s3nymxs34l7o">
                <MapPin className="w-5 h-5 text-red-500 mr-3" data-id="element-240" data-mp="1n8rvb1heebf9" />
                Nuestras Sedes
              </h3>
              <div className="grid sm:grid-cols-2 gap-8" data-id="element-241" data-mp="1i9fb6ohwvigd">
                <div data-id="element-242" data-mp="1ebhkticemqbe">
                  <h4 className="text-white font-medium mb-2" data-id="element-243" data-mp="1e83fq2ccw309">
                    Medellín (Principal)
                  </h4>
                  <p className="text-slate-400 text-sm font-light" data-id="element-244" data-mp="261ly6x1qyd34">
                    Calle 33 # 83-16 Int. 201
                  </p>
                </div>
                <div data-id="element-245" data-mp="21y7kid1a68su">
                  <h4 className="text-white font-medium mb-2" data-id="element-246" data-mp="221lkxfqdw7xn">Bogotá</h4>
                  <p className="text-slate-400 text-sm font-light" data-id="element-247" data-mp="1x05xjh0qhpc3">
                    Calle 93 # 11A-28 Oficina 601
                  </p>
                </div>
                <div data-id="element-248" data-mp="lkzem8gdw8tr">
                  <h4 className="text-white font-medium mb-2" data-id="element-249" data-mp="lkpq6pazm9q6">Barranquilla</h4>
                  <p className="text-slate-400 text-sm font-light" data-id="element-250" data-mp="2x9niukd6nc7a">
                    Carrera 52 No. 69-96 Oficina 401
                  </p>
                </div>
                <div data-id="element-251" data-mp="325l2azjqsyoa">
                  <h4 className="text-white font-medium mb-2" data-id="element-252" data-mp="3690unjos4cv6">Pereira</h4>
                  <p className="text-slate-400 text-sm font-light" data-id="element-253" data-mp="367ppbffoqx1y">
                    Carrera 25 # 86-71 Torre 1 Int. 301
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 7.4 Bloque de Confianza */}
          <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left space-y-4 sm:space-y-0 sm:space-x-4 bg-white/5 border border-white/10 rounded-sm p-6 max-w-3xl mx-auto" data-id="element-254" data-mp="3kx5if22ovekt">
            <Clock className="w-8 h-8 text-red-500 flex-shrink-0" data-id="element-255" data-mp="3tybzbc992rel" />
            <p className="text-slate-300 font-light text-lg" data-id="element-256" data-mp="1vkk324to4q10">
              <strong className="text-white font-medium" data-id="element-257" data-mp="1zl40zam2fxo7">
                Respondemos en menos de 24 horas.
              </strong>{' '}
              Atención personalizada y estrictamente confidencial.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER PREMIUM */}
      <footer className="bg-[#05080f] py-16 text-center text-slate-500 text-sm border-t border-white/5" data-id="element-258" data-mp="1pzgr3mdeyxei">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center" data-id="element-259" data-mp="1g7srgy6pea1y">
          <img src={logoUrl} alt="Standard Consulting Logo" loading="lazy" className="h-10 w-auto mb-10 mix-blend-screen opacity-50 hover:opacity-100 transition-all grayscale hover:grayscale-0" data-id="element-260" data-mp="1g5q30il78ozc" />
          <div className="flex space-x-6 mb-8 text-xs tracking-widest uppercase" data-id="element-261" data-mp="1uxi69w7pyckp">
            <a href="#" className="hover:text-white transition-colors" data-id="element-262" data-mp="1zvaa9oln1p59">
              Aviso de Privacidad
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-263" data-mp="jgifx64xm7tb">
              Términos y Condiciones
            </a>
          </div>
          <p className="font-light" data-id="element-264" data-mp="oek05ue3gd6d">
            © {new Date().getFullYear()} Standard Consulting. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      {/* FLOATING WHATSAPP BUTTON */}
      <a href="https://wa.me/573146152758" target="_blank" rel="noopener noreferrer" aria-label="Contactar por WhatsApp" className="fixed bottom-6 left-6 z-50 group flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-green-500/40 hover:scale-110 hover:shadow-green-500/60 transition-all duration-300" data-id="element-265" data-mp="pxsfcm8ptj7h">
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" data-id="element-266" data-mp="3odl4qsyxra9s"></span>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-7 h-7 relative z-10" data-id="element-267" data-mp="70r6urfyd0ys">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-268" data-mp="3amgdof9mvqb2" />
        </svg>
      </a>
    </div>;
}